import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetProductSalesUnits } from "../../../api/product";
import {
  useGetAverageProductSummary,
  useGetProductSummariesForNodes,
  useGetProductSummaryHistoryById,
  useGetProductSummaryHistoryDropDownData
} from "../../../api/productSummary";
import { NOT_APPLICABLE } from "../../../config/constants";
import { humanize, objectKeyValueList } from "../../../utils/utils";
import { formatCalculatedDate, getStageColor, getStageSubtitle, getStageTooltip } from "../helpers";
import { LifecycleStage, ProcessingStep, ProductSummaryData } from "../types";

/**
 * Custom hook for URL query parameters
 * Single Responsibility: URL query parsing
 */
export const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

/**
 * Custom hook for managing product summary state
 * Single Responsibility: State management
 */
export const useProductSummaryState = () => {
  const [salesUnitId, setSalesUnitId] = useState<string>();
  const [productId, setProductId] = useState<string>();
  const [searchKey, setSearchKey] = useState("");
  const [productSummaryErrors, setProductSummaryErrors] = useState<string[]>([]);
  const [productSummaryWarnings, setProductSummaryWarnings] = useState<string[]>([]);
  const [barChatContentView, setBarChatContentView] = useState<boolean>(false);
  const [productSummaryId, setProductSummaryId] = useState<string>("");
  const [productSummary, setProductSummary] = useState<ProductSummaryData | null>(null);
  const [isAverageProduct, setIsAverageProduct] = useState<any>(undefined);
  const [viewProductSummaryDetailsModal, setProductSummaryDetailsModal] = useState(false);
  const [productSummaryHistoryModal, setProductSummaryHistoryModal] = useState(false);

  return {
    salesUnitId,
    setSalesUnitId,
    productId,
    setProductId,
    searchKey,
    setSearchKey,
    productSummaryErrors,
    setProductSummaryErrors,
    productSummaryWarnings,
    setProductSummaryWarnings,
    barChatContentView,
    setBarChatContentView,
    productSummaryId,
    setProductSummaryId,
    productSummary,
    setProductSummary,
    isAverageProduct,
    setIsAverageProduct,
    viewProductSummaryDetailsModal,
    setProductSummaryDetailsModal,
    productSummaryHistoryModal,
    setProductSummaryHistoryModal
  };
};

/**
 * Custom hook for fetching and processing product summary data
 * Single Responsibility: Data fetching and transformation
 */
export const useProductSummaryData = (
  productId: string | undefined,
  salesUnitId: string | undefined,
  productSummaryId: string,
  isAverageProduct: boolean | undefined,
  searchKey: string,
  setProductSummary: (data: ProductSummaryData) => void
) => {
  const { data: productSalesUnitsData, isLoading: isProductSalesUnitsLoading } =
    useGetProductSalesUnits({ searchKey, isWithAverageProduct: true });

  const {
    data: productSummaryDropdownData,
    isFetching: isProductSummaryHistoryLoading,
    refetch: refetchProductSummaryDropdownData
  } = useGetProductSummaryHistoryDropDownData({
    productId,
    salesUnitId,
    productIsAverage: isAverageProduct
  });

  const { isFetching: isAverageProductSummaryLoading, refetch: refetchAverageProductSummary } =
    useGetAverageProductSummary(
      {
        productId,
        productIsAverage: isAverageProduct
      },
      {
        onSuccess: data => {
          setProductSummary(data);
        }
      }
    );

  const { isFetching: isProductSummaryDataLoading, refetch: refetchProductSummaryHistoryById } =
    useGetProductSummaryHistoryById(
      { productSummaryId, productIsAverage: isAverageProduct },
      {
        onSuccess: data => {
          setProductSummary(data);
        }
      }
    );

  const { data: productSummariesForNode, isLoading: isProductSummariesLoading } =
    useGetProductSummariesForNodes(productSummaryId);

  return {
    productSalesUnitsData,
    isProductSalesUnitsLoading,
    productSummaryDropdownData,
    isProductSummaryHistoryLoading,
    isAverageProductSummaryLoading,
    isProductSummaryDataLoading,
    productSummariesForNode,
    isProductSummariesLoading,
    refetchProductSummaryDropdownData,
    refetchAverageProductSummary,
    refetchProductSummaryHistoryById
  };
};

/**
 * Custom hook for processing lifecycle stages data
 * Single Responsibility: Lifecycle stage transformation
 */
export const useLifecycleStages = (productSummary: ProductSummaryData | null): LifecycleStage[] => {
  const cycleStageData: any = productSummary?.cycleStages || {};
  const cycleStages = Object.entries(cycleStageData)
    .filter(([key]) => {
      const normalizedKey = key.toLowerCase().replace(/_/g, "");
      return normalizedKey !== "cultivationimpact";
    })
    .map(([key, value]) => ({
      key,
      value
    }));

  return useMemo(() => {
    return cycleStages.map(({ key, value }) => ({
      label: humanize(key),
      subtitle: getStageSubtitle(key),
      pct: value === NOT_APPLICABLE ? 0 : parseFloat(value as string),
      color: getStageColor(key),
      tooltip: getStageTooltip(key),
      drilldown: []
    }));
  }, [cycleStages]);
};

/**
 * Custom hook for formatting calculated date
 * Single Responsibility: Date calculation
 */
export const useCalculatedDate = (productSummaryDropdownData: any): string => {
  return useMemo(() => {
    const item = productSummaryDropdownData?.data?.[0];
    return formatCalculatedDate(item?.createdAt);
  }, [productSummaryDropdownData]);
};

/**
 * Custom hook for chart values
 * Single Responsibility: Chart data transformation
 */
export const useChartValues = (productSummariesForNode: any) => {
  return useMemo(() => {
    const [names, values] = objectKeyValueList(productSummariesForNode);
    return [names, values];
  }, [productSummariesForNode]);
};

/**
 * Custom hook transforming node-summary-impacts data into processing-step chart rows.
 * Single Responsibility: Processing-steps chart data transformation
 */
export const useProcessingSteps = (productSummariesForNode: any): ProcessingStep[] => {
  const chartValues = useChartValues(productSummariesForNode);
  return useMemo(() => {
    if (!chartValues || !chartValues[0] || !chartValues[1]) return [];
    const [names, values] = chartValues;
    return (names as string[]).map((name, index) => ({
      name,
      impact: Number(values[index]) || 0
    }));
  }, [chartValues]);
};

/**
 * Custom hook for product summary URL and navigation effects
 * Single Responsibility: URL synchronization
 */
export const useProductSummaryEffects = (
  salesUnitId: string | undefined,
  productId: string | undefined,
  isAverageProduct: boolean | undefined,
  productSummary: ProductSummaryData | null,
  productSalesUnitsData: any,
  productSummaryDropdownData: any,
  setSalesUnitId: (id: string | undefined) => void,
  setProductId: (id: string | undefined) => void,
  setIsAverageProduct: (isAverage: boolean | undefined) => void,
  setProductSummaryId: (id: string) => void,
  setProductSummaryErrors: (errors: string[]) => void,
  setProductSummaryWarnings: (warnings: string[]) => void
) => {
  const query = useQuery();
  const navigate = useNavigate();

  useEffect(() => {
    const salesUnitIdParam = query.get("salesUnitId");
    const productIdParam = query.get("productId");
    const isAverageProductParam = query.get("isAverageProduct") === "true";
    setIsAverageProduct(isAverageProductParam);
    if (salesUnitIdParam && productIdParam) {
      setSalesUnitId(salesUnitIdParam);
      setProductId(productIdParam);
    }
  }, []);

  useEffect(() => {
    const productData = productSalesUnitsData?.find(
      (productSalesUnit: any) => productSalesUnit.salesUnitId === salesUnitId
    );
    const isAverageProductData = productData?.isAverageProduct;
    setIsAverageProduct(isAverageProductData);
  }, [salesUnitId, productSalesUnitsData]);

  useEffect(() => {
    if (salesUnitId && productId) {
      navigate(
        `/product-summary?salesUnitId=${salesUnitId}&productId=${productId}&isAverageProduct=${isAverageProduct}`
      );
    }
  }, [salesUnitId, productId, isAverageProduct, navigate]);

  useEffect(() => {
    setProductSummaryId(productSummaryDropdownData?.data?.[0]?._id || "");
  }, [productSummaryDropdownData]);

  useEffect(() => {
    if (productSummary?.errors) {
      setProductSummaryErrors(productSummary.errors);
    }
    if (productSummary?.warnings) {
      setProductSummaryWarnings(productSummary.warnings);
    }
  }, [productSummary]);
};
